import { useState, useRef, useCallback } from "react";
import {
  useExpenses, EXPENSE_CATEGORIES, PAYMENT_METHODS,
  ExpenseCategory, PaymentMethod, ExpenseStatus,
} from "@/contexts/ExpenseContext";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  Plus, Search, IndianRupee, TrendingUp, CheckCircle2, XCircle,
  Receipt, Paperclip, Trash2, Filter, BarChart3, Calendar,
  Briefcase, Plane, Zap, Users, Megaphone, MoreHorizontal, Eye
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

function formatINR(v: number) { return `₹${v.toLocaleString("en-IN", { minimumFractionDigits: 0 })}`; }

const CATEGORY_ICONS: Record<ExpenseCategory, React.ComponentType<{ className?: string }>> = {
  Office: Briefcase, Travel: Plane, Utilities: Zap,
  Salary: Users, Marketing: Megaphone, Miscellaneous: MoreHorizontal,
};

const CATEGORY_COLORS: Record<ExpenseCategory, string> = {
  Office: "bg-primary/10 text-primary", Travel: "bg-accent/10 text-accent",
  Utilities: "bg-warning/10 text-warning", Salary: "bg-success/10 text-success",
  Marketing: "bg-info/10 text-info", Miscellaneous: "bg-muted text-muted-foreground",
};

const STATUS_STYLES: Record<ExpenseStatus, string> = {
  Draft: "bg-muted text-muted-foreground",
  Submitted: "bg-info/10 text-info border-info/20",
  Approved: "bg-success/10 text-success border-success/20",
  Rejected: "bg-destructive/10 text-destructive border-destructive/20",
};

const ExpensesPage = () => {
  const { expenses, addExpense, deleteExpense, approveExpense, rejectExpense, updateExpense, getMonthlyBreakdown, getTotalByCategory } = useExpenses();
  const { role, user } = useAuth();
  const { toast } = useToast();
  const [tab, setTab] = useState("list");
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState<string>("all");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [formOpen, setFormOpen] = useState(false);
  const [receiptViewUrl, setReceiptViewUrl] = useState<string | null>(null);
  const receiptRef = useRef<HTMLInputElement>(null);

  // Form state
  const [fDate, setFDate] = useState(new Date().toISOString().split("T")[0]);
  const [fAmount, setFAmount] = useState(0);
  const [fCategory, setFCategory] = useState<ExpenseCategory>("Office");
  const [fVendor, setFVendor] = useState("");
  const [fDesc, setFDesc] = useState("");
  const [fPayment, setFPayment] = useState<PaymentMethod>("Cash");
  const [fRef, setFRef] = useState("");
  const [fReceipt, setFReceipt] = useState<{ url: string; name: string } | null>(null);

  const canApprove = role === "ca" || role === "company_admin";
  const totalByCategory = getTotalByCategory();
  const monthlyData = getMonthlyBreakdown();
  const totalExpenses = expenses.filter((e) => e.status !== "Rejected").reduce((s, e) => s + e.amount, 0);
  const pendingCount = expenses.filter((e) => e.status === "Submitted").length;

  const filtered = expenses.filter((e) => {
    const matchSearch = e.vendor.toLowerCase().includes(search.toLowerCase()) ||
      e.description.toLowerCase().includes(search.toLowerCase());
    const matchCat = filterCat === "all" || e.category === filterCat;
    const matchStatus = filterStatus === "all" || e.status === filterStatus;
    return matchSearch && matchCat && matchStatus;
  });

  const handleReceiptUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "File too large", description: "Max 5MB", variant: "destructive" }); return;
    }
    const url = await new Promise<string>((res) => {
      const reader = new FileReader();
      reader.onload = () => res(reader.result as string);
      reader.readAsDataURL(file);
    });
    setFReceipt({ url, name: file.name });
    e.target.value = "";
  }, [toast]);

  const handleSubmit = () => {
    if (!fVendor.trim() || fAmount <= 0) {
      toast({ title: "Error", description: "Vendor and amount are required", variant: "destructive" }); return;
    }
    addExpense({
      date: fDate, amount: fAmount, category: fCategory,
      vendor: fVendor.trim(), description: fDesc.trim(),
      paymentMethod: fPayment, reference: fRef || undefined,
      receiptUrl: fReceipt?.url, receiptName: fReceipt?.name,
    });
    toast({ title: "Expense Recorded", description: `${formatINR(fAmount)} — ${fCategory}` });
    setFormOpen(false);
    setFAmount(0); setFVendor(""); setFDesc(""); setFRef(""); setFReceipt(null);
  };

  const handleStatusChange = (id: string, newStatus: "Submitted") => {
    updateExpense(id, { status: newStatus });
    toast({ title: "Expense submitted for approval" });
  };

  const inputCls = "w-full px-3 py-2 text-sm rounded-md border border-border bg-card outline-none focus:ring-1 focus:ring-accent";
  const labelCls = "text-xs font-medium text-muted-foreground uppercase tracking-wider block mb-1.5";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold font-display text-foreground">Expenses</h1>
          <p className="text-sm text-muted-foreground">Record, categorize, and track business expenses</p>
        </div>
        <Dialog open={formOpen} onOpenChange={setFormOpen}>
          <DialogTrigger asChild>
            <Button className="bg-accent text-accent-foreground hover:bg-accent/90"><Plus className="h-4 w-4 mr-1" /> New Expense</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="font-display">Record Expense</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><label className={labelCls}>Date</label><input type="date" value={fDate} onChange={(e) => setFDate(e.target.value)} className={inputCls} /></div>
                <div><label className={labelCls}>Amount (₹)</label><input type="number" min="0" value={fAmount} onChange={(e) => setFAmount(Number(e.target.value))} className={inputCls} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={labelCls}>Category</label>
                  <Select value={fCategory} onValueChange={(v) => setFCategory(v as ExpenseCategory)}><SelectTrigger className="text-sm"><SelectValue /></SelectTrigger><SelectContent>{EXPENSE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
                </div>
                <div><label className={labelCls}>Payment Method</label>
                  <Select value={fPayment} onValueChange={(v) => setFPayment(v as PaymentMethod)}><SelectTrigger className="text-sm"><SelectValue /></SelectTrigger><SelectContent>{PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select>
                </div>
              </div>
              <div><label className={labelCls}>Vendor</label><input value={fVendor} onChange={(e) => setFVendor(e.target.value)} maxLength={100} className={inputCls} placeholder="Vendor name" /></div>
              <div><label className={labelCls}>Description</label><input value={fDesc} onChange={(e) => setFDesc(e.target.value)} maxLength={200} className={inputCls} placeholder="What was this for?" /></div>
              <div><label className={labelCls}>Reference</label><input value={fRef} onChange={(e) => setFRef(e.target.value)} maxLength={50} className={inputCls} placeholder="Invoice/receipt number" /></div>
              <div>
                <label className={labelCls}>Attach Receipt</label>
                <div className="flex items-center gap-3">
                  <button onClick={() => receiptRef.current?.click()} className="flex items-center gap-2 px-3 py-2 rounded-md border border-dashed border-border text-xs text-muted-foreground hover:border-accent hover:text-accent transition-colors">
                    <Paperclip className="h-3.5 w-3.5" /> {fReceipt ? fReceipt.name : "Choose file (PDF, JPG, PNG)"}
                  </button>
                  {fReceipt && <button onClick={() => setFReceipt(null)} className="text-xs text-destructive">Remove</button>}
                </div>
                <input ref={receiptRef} type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleReceiptUpload} className="hidden" />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setFormOpen(false)}>Cancel</Button>
                <Button onClick={handleSubmit} className="bg-accent text-accent-foreground">Save Expense</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-border/50">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center"><IndianRupee className="h-5 w-5 text-primary" /></div>
            <div><p className="text-lg font-bold text-foreground">{formatINR(totalExpenses)}</p><p className="text-[11px] text-muted-foreground">Total Expenses</p></div>
          </CardContent>
        </Card>
        <Card className="border-border/50">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center"><Receipt className="h-5 w-5 text-accent" /></div>
            <div><p className="text-lg font-bold text-foreground">{expenses.length}</p><p className="text-[11px] text-muted-foreground">Total Entries</p></div>
          </CardContent>
        </Card>
        <Card className="border-border/50">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center"><TrendingUp className="h-5 w-5 text-warning" /></div>
            <div><p className="text-lg font-bold text-foreground">{pendingCount}</p><p className="text-[11px] text-muted-foreground">Pending Approval</p></div>
          </CardContent>
        </Card>
        <Card className="border-border/50">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center"><CheckCircle2 className="h-5 w-5 text-success" /></div>
            <div><p className="text-lg font-bold text-foreground">{expenses.filter((e) => e.status === "Approved").length}</p><p className="text-[11px] text-muted-foreground">Approved</p></div>
          </CardContent>
        </Card>
      </div>

      {/* Category Cards */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
        {EXPENSE_CATEGORIES.map((cat) => {
          const Icon = CATEGORY_ICONS[cat];
          return (
            <button key={cat} onClick={() => { setFilterCat(cat); setTab("list"); }} className={`p-3 rounded-lg border border-border/50 text-center hover:shadow-soft transition-all ${filterCat === cat ? "ring-1 ring-accent" : ""}`}>
              <div className={`w-8 h-8 rounded-lg mx-auto mb-1.5 flex items-center justify-center ${CATEGORY_COLORS[cat]}`}>
                <Icon className="h-4 w-4" />
              </div>
              <p className="text-[10px] font-medium text-foreground">{cat}</p>
              <p className="text-[10px] text-muted-foreground">{formatINR(totalByCategory[cat])}</p>
            </button>
          );
        })}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="list" className="text-xs">All Expenses</TabsTrigger>
          <TabsTrigger value="reports" className="text-xs">Monthly Reports</TabsTrigger>
        </TabsList>

        {/* LIST TAB */}
        <TabsContent value="list">
          <Card className="border-border/50">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <CardTitle className="text-base">Expense Entries</CardTitle>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2 bg-card border border-border rounded-md px-3 py-1.5">
                    <Search className="h-3.5 w-3.5 text-muted-foreground" />
                    <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search..." className="bg-transparent text-xs outline-none w-32 text-foreground placeholder:text-muted-foreground" />
                  </div>
                  <Select value={filterCat} onValueChange={setFilterCat}>
                    <SelectTrigger className="text-xs w-28 h-8"><Filter className="h-3 w-3 mr-1" /><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {EXPENSE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="text-xs w-28 h-8"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      {(["Draft", "Submitted", "Approved", "Rejected"] as ExpenseStatus[]).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto max-h-[500px]">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/30">
                      {["Date", "Vendor", "Category", "Description", "Payment", "Amount", "Receipt", "Status", "Actions"].map((h) => (
                        <TableHead key={h} className="text-[11px] font-semibold">{h}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.length === 0 ? (
                      <TableRow><TableCell colSpan={9} className="text-center py-8 text-sm text-muted-foreground">No expenses found</TableCell></TableRow>
                    ) : filtered.map((exp) => {
                      const CatIcon = CATEGORY_ICONS[exp.category];
                      return (
                        <TableRow key={exp.id} className="hover:bg-muted/20">
                          <TableCell className="text-xs whitespace-nowrap">{exp.date}</TableCell>
                          <TableCell className="text-xs font-medium">{exp.vendor}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={`text-[10px] gap-1 ${CATEGORY_COLORS[exp.category]}`}>
                              <CatIcon className="h-3 w-3" />{exp.category}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground max-w-[180px] truncate">{exp.description}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{exp.paymentMethod}</TableCell>
                          <TableCell className="text-xs font-semibold text-foreground">{formatINR(exp.amount)}</TableCell>
                          <TableCell className="text-center">
                            {exp.receiptUrl ? (
                              <Dialog>
                                <DialogTrigger asChild>
                                  <button onClick={() => setReceiptViewUrl(exp.receiptUrl!)} className="text-accent hover:text-accent/80"><Eye className="h-3.5 w-3.5" /></button>
                                </DialogTrigger>
                                <DialogContent className="max-w-3xl">
                                  <DialogHeader><DialogTitle className="text-sm">{exp.receiptName || "Receipt"}</DialogTitle></DialogHeader>
                                  <div className="flex items-center justify-center min-h-[300px]">
                                    {exp.receiptUrl?.startsWith("data:image") ? (
                                      <img src={exp.receiptUrl} alt="Receipt" className="max-w-full max-h-[60vh] object-contain rounded-lg" />
                                    ) : (
                                      <iframe src={exp.receiptUrl} title="Receipt" className="w-full h-[60vh] rounded-lg" />
                                    )}
                                  </div>
                                </DialogContent>
                              </Dialog>
                            ) : <span className="text-[10px] text-muted-foreground">—</span>}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={`text-[10px] ${STATUS_STYLES[exp.status]}`}>{exp.status}</Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              {exp.status === "Draft" && (
                                <button onClick={() => handleStatusChange(exp.id, "Submitted")} className="p-1 rounded text-info hover:bg-info/10 text-[10px]" title="Submit">Submit</button>
                              )}
                              {exp.status === "Submitted" && canApprove && (
                                <>
                                  <button onClick={() => approveExpense(exp.id)} className="p-1 rounded text-success hover:bg-success/10"><CheckCircle2 className="h-3.5 w-3.5" /></button>
                                  <button onClick={() => rejectExpense(exp.id)} className="p-1 rounded text-destructive hover:bg-destructive/10"><XCircle className="h-3.5 w-3.5" /></button>
                                </>
                              )}
                              {(exp.status === "Draft" || exp.status === "Rejected") && (
                                <button onClick={() => deleteExpense(exp.id)} className="p-1 rounded text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* REPORTS TAB */}
        <TabsContent value="reports">
          <div className="space-y-6">
            {monthlyData.map((month) => (
              <Card key={month.month} className="border-border/50">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-accent" /> {month.label}
                    </CardTitle>
                    <span className="text-lg font-bold text-primary">{formatINR(month.total)}</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {EXPENSE_CATEGORIES.map((cat) => {
                      const amount = month.byCategory[cat];
                      if (amount <= 0) return null;
                      const Icon = CATEGORY_ICONS[cat];
                      const pct = month.total > 0 ? Math.round((amount / month.total) * 100) : 0;
                      return (
                        <div key={cat} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 border border-border/30">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${CATEGORY_COLORS[cat]}`}>
                            <Icon className="h-4 w-4" />
                          </div>
                          <div className="flex-1">
                            <div className="flex justify-between items-center">
                              <p className="text-xs font-medium">{cat}</p>
                              <p className="text-xs font-semibold">{formatINR(amount)}</p>
                            </div>
                            <div className="mt-1 h-1.5 rounded-full bg-border overflow-hidden">
                              <div className="h-full rounded-full bg-accent transition-all" style={{ width: `${pct}%` }} />
                            </div>
                            <p className="text-[10px] text-muted-foreground mt-0.5">{pct}% of total</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ))}
            {monthlyData.length === 0 && (
              <Card className="border-dashed border-2 border-border">
                <CardContent className="flex flex-col items-center justify-center py-16 gap-3">
                  <BarChart3 className="h-12 w-12 text-muted-foreground/30" />
                  <p className="text-sm text-muted-foreground">No expense data to report</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ExpensesPage;
